import { defineStore } from 'pinia';

import localeCurrency from 'locale-currency';

import { useAppStore } from './app';

import * as ComponentUtilities from '../helpers/components';

import * as ThreeUtilities from '../components/Three/threeutilities';

import emitter from '../helpers/bus';

import * as shopify from './shopify';

function getServiceUrl(service, options) {
    const config = new options.DriveCustomizer.Configuration();

    return `${config.endpoint}extension-acushnet-myjoys/${service}?apikey=${options.apiKey}`;
}

// Formats the price according to current locale settings.
function formatPrice(v, relative, locale, currency) {
    if (v == null || v === '' || v === 0) {
        return '';
    }

    const resolveCurrency = currency ?? localeCurrency.getCurrency(locale) ?? 'USD';

    const wholeFormatter = Intl.NumberFormat(locale ?? 'en-US', {
        style: 'currency',
        currencyDisplay: 'narrowSymbol',
        notation: 'standard',
        currency: resolveCurrency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    });

    const fractionFormatter = Intl.NumberFormat(locale ?? 'en-US', {
        style: 'currency',
        currencyDisplay: 'narrowSymbol',
        notation: 'standard',
        currency: resolveCurrency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    const formatted = v % 1 === 0 ? wholeFormatter.format(v) : fractionFormatter.format(v);

    if (relative) {
        if (formatted.startsWith('-')) {
            return formatted;
        }

        return `+${formatted}`;
    }

    return formatted;
}

// eslint-disable-next-line import/prefer-default-export
export const useCustomizerStore = defineStore('customizer', {
    id: 'customizer',

    state: () => ({
        // Widget configuration options.
        options: {},

        // Currently selected controller.
        viewController: null,

        // Current vendor data.
        vendor: null,

        // Branding programs for the current vendor.
        brandingPrograms: null,

        // Personalization policy modal content.
        personalizationPolicy: null,

        // Size guide table.
        sizeGuide: null,

        // Size translations for the current vendor.
        sizeTranslations: null,

        // Used to modify the UI for locales with longer strings
        isWordyLocale: false,

        // Other translations according to the vendor locale.
        otherTranslations: null,

        // Yet more other translations according to the vendor locale.
        fallbackTranslations: null,

        // This message will be displayed in the PromoMessage component if present.
        specialMessage: null,

        // Complete product data provided by WebTop.
        product: null,

        productSku: null,

        gemSkus: null,

        // Initial instance saved by WebTop.
        initialInstance: null,

        // The very first controller that would allow to reset all selections to the very beginning.
        startingController: null,

        // A stack of captured controllers representing user selections.
        undoStack: [],

        cartErrors: 0,

        inErrorState: false,

        // Used to display an error message in the accordion when a selected size is backordered
        hasBackorderedError: false,

        // Used to disable the Add to Cart button when a selected size is backordered
        hasBackorderedSize: false,

        // The currently selected group
        currentGroup: null,

        // The camera view for the selected group
        currentView: null,

        // Used to display a modal that points to the login screen if the user isn't logged in
        addToWishlistMessage: null,

        // Did the last save instance operation resulted in errors?
        instanceError: false,

        // Is the product available?
        notAvailable: false,

        // Mirror personalization options.
        mirrorPersonalization: false,

        products: {},

        variants: [],

        orderableVariant: null,
        orderableBase: null,
        cartItems: null,

        previewMode: '2d',
    }),

    getters: {
        /**
         * A list of groups visible in the UI.
         */
        visibleGroups() {
            // Safety measures, use empty list if there is no controller.
            if (this.viewController == null) {
                return [];
            }

            const groups = this.viewController.blueprint?.groups?.filter((group) => {
                // Skip groups that are hidden in the UI.
                if (group?.custom?.['fj-hidden'] === 'true') {
                    return false;
                }

                // Skip groups with no visible placements.
                const placements = this.visiblePlacements(group);

                if (placements.length === 0) {
                    return false;
                }

                return true;
            });

            return groups ?? [];
        },

        /**
         * Calculate a list of visible placements by group.
         */
        // eslint-disable-next-line no-unused-vars
        visiblePlacementsByGroup() {
            // Safety measures, use empty list if there is no controller.
            if (this.viewController == null) {
                return {};
            }

            const groups = {};

            this.visibleGroups.forEach((group) => {
                groups[group.code] = this.visiblePlacements(group);
            });

            return groups;
        },

        /**
         * The name of the shoe being customized.
         */
        productName() {
            if (this.viewController?.blueprint?.custom?.['fj-override-name']) {
                return this.viewController.blueprint.custom['fj-override-name'];
            }

            // Are there fallback translations?
            return this.useTranslations(`${this.productSku}_label`, this.viewController?.blueprint.name);
        },

        /**
         * Extract product price from WebTop data.
         */
        productPrice() {
            if (this.product == null) {
                return '';
            }

            if (this.product.map == null) {
                return '';
            }

            const { currency, locale } = this.options;

            // For example,
            // currency: 'USD',
            // locale: 'en-US'

            const price = formatPrice(parseFloat(this.product.map), false, locale, currency);

            return price;
        },

        /**
         * The gender/department of the shoe.
         */
        productDepartment() {
            return this.viewController?.blueprint?.custom?.['fj-gender'];
        },

        /**
         * Selections made so far.
         */
        selectionsMade() {
            const selections = [];

            this.visibleGroups.forEach((group) => {
                let totalPlacements = group.placements.length;
                let totalPlacementSelections = 0;

                if (this.isPersonalizationGroup(group)) {
                    totalPlacements = 1;

                    if (this.selectedPersonalizationType(group) === 'personalization-none') {
                        totalPlacementSelections = 1;
                    } else {
                        const selected = this.selectedPersonalization(group);

                        if (selected) {
                            totalPlacementSelections = selected.placeholder ? 0 : 1;
                        }
                    }
                } else {
                    group.placements.forEach((placement) => {
                        const selectedAtPlacement = this.viewController.selectedAtPlacement(placement.code);

                        if (selectedAtPlacement && !selectedAtPlacement.placeholder) {
                            totalPlacementSelections += 1;
                        }
                    });
                }

                if (totalPlacementSelections === totalPlacements) {
                    selections.push(group.name);
                }
            });

            return selections;
        },

        /**
         * Total selections made.
         */
        totalSelectionsMade() {
            return this.selectionsMade.length;
        },

        /**
         * Determines whether all required selections have been made.
         * Determines whether the Add to Cart button is enabled.
         */
        selectionsComplete() {
            return this.totalSelectionsMade === this.visibleGroups.length;
        },

        /**
         * Determines whether a selection has been made by the user.
         */
        userMadeSelection() {
            return this.undoStack.length > 0;
        },

        /**
         * Computed getter to translate things using translations provided by vendor data.
         */
        useTranslations() {
            return (key, defaultText) => {
                // No translations loaded yet.
                if (this.fallbackTranslations == null) {
                    return defaultText;
                }

                let useKey = key;

                // Override BOA colors.
                if (useKey === 'lacecolor' && this.viewController.blueprint.custom['fj-boa'] === 'boa') {
                    useKey = 'boacolor';
                }

                if (this.fallbackTranslations[useKey]) {
                    let useText = this.fallbackTranslations[useKey];

                    useText = useText.replace('Boa ', 'BOA ');

                    return useText;
                }

                return defaultText;
            };
        },

        /**
         * Checks if request originated from cart.
         */
        isFromCart() {
            const id = this.options?.lineItemId;

            if (id && id.length > 0) {
                return true;
            }

            return false;
        },

        /**
         * Gets a list of allowed logos.
         */
        allowedLogos() {
            const allowed = new Set();

            if (this.product?.logos) {
                this.product.logos.forEach((logoGroup) => {
                    if (logoGroup.logos) {
                        logoGroup.logos.forEach((logo) => {
                            allowed.add(logo.ecomValue);
                        });
                    }
                });
            }

            return allowed;
        },

        /**
         * Gets base site URL (which is hopefully the current site) using the add to cart link.
         */
        baseSiteUrl() {
            let serviceUrl = null;

            if (this.vendor?.cartUrl) {
                serviceUrl = this.vendor.cartUrl;
            }

            if (this.options.cartUrl) {
                serviceUrl = this.options.cartUrl;
            }

            if (serviceUrl) {
                const lastSlash = serviceUrl.lastIndexOf('/');

                if (lastSlash > 0) {
                    serviceUrl = serviceUrl.substr(0, lastSlash + 1);
                }
            }

            return serviceUrl;
        },
    },

    actions: {
        /**
         * Save options.
         */
        setOptions(options) {
            this.options = options;

            // Replace empty values that we are getting from SFCC with proper nulls.
            if (this.options.products === '') {
                this.options.products = null;
            }

            if (this.options.package === '') {
                this.options.package = null;
            }
        },

        /**
         * Start loading products.
         */
        load(options) {
            if (options) {
                Object.assign(this.options, options);
            }

            this.loadCustomizeFlow();
        },

        /**
         * Calculate default controller options.
         */
        defaultControllerOptions() {
            const options = {
                apiKey: this.options.apiKey,
                products: this.options.products,
                preview: this.options.preview,
                site: this.options.site,
                version: this.options.version,
                locale: this.options.locale,
                disableCaching: this.options.disableCaching,
            };

            if (this.options.endpoint) {
                options.endpoint = this.options.endpoint;
            }

            return options;
        },

        /**
         * Loads and availability for components mentioned in the blueprint.
         */
        initializeAvailability() {
            return new Promise((resolve) => {
                resolve();
            });
        },

        /**
         * Retrieve a component price.
         */
        // eslint-disable-next-line no-unused-vars
        getPrice({ component }) {
            return new Promise((resolve) => {
                resolve(10);
            });
        },

        /**
         * Attaches events to the controller.
         */
        attachControllerEvents(controller) {
            controller.on('setReactive', (v) => {
                // Vue 3 doesn't need Vue.set anymore.
                v.target[v.key] = v.value;
                return true;
            });

            controller.on('initializeAvailability', (o) => this.initializeAvailability({
                controller: o.controller,
                blueprint: o.blueprint,
            }));

            controller.on('beforeTransferCompatibleState', (o) => {
                this.initializeFromAnotherState(o.controller, o.from);
            });

            controller.on('beforeReloadBlueprint', () => {
                const appStore = useAppStore();

                appStore.setIsLoading(true);

                emitter.$emit('capturePoster', {
                    duration: 15000,
                    preventRelease: true,
                });
            });

            controller.on('isAvailable', (component) => this.isAvailable(component));

            controller.on('getPrice', (component) => this.getPrice({
                component,
                alwaysAvailable: this.options.alwaysAvailable,
                testPrices: this.options.testPrices,
                ignoreOnline: this.options.ignoreOnline,
                products: this.products,
            }));
        },

        /**
         * Load controller.
         */
        loadController(controller) {
            this.attachControllerEvents(controller);

            controller.load();
        },

        /**
         * Check if the component is available according to the current product configuration.
         */
        isAvailable() {
            return true;
        },

        /**
         * Since we are using product-specific option IDs, some placement selections cannot be copied cleanly to the new state,
         * so we need to resolve them manually.
         */
        initializeFromAnotherState() {
        },

        /**
         * Load customization flow.
         */
        loadCustomizeFlow() {
            const controller = new this.options.DriveCustomizer.Controller({
                ...this.defaultControllerOptions(),
            });

            controller.on('afterInitializeBlueprint', () => {
                // Load actual product options.
                this.loadColors({
                    controllers: [controller],
                });
            });

            controller.on('stateChanged', () => {
                this.stateChanged();
            });

            // Load controller.
            this.loadController(controller);
        },

        stateChanged() {
            const sku = [];

            this.viewController.state.forEach((s) => {
                if (s.placement.custom?.['baa-sku'] === 'true') {
                    sku.push(s.component.placementIndependentCode);
                }
            });

            this.productSku = sku.join('-');

            const productHandle = [];

            this.viewController.state.forEach((s) => {
                if (s.component.custom?.['baa-product-handle']) {
                    productHandle.push(s.component.custom?.['baa-product-handle']);
                }
            });

            if (productHandle.length > 0) {
                const productId = productHandle.join('-');

                if (this.products[productId]) {
                    this.checkOrderableSku();
                } else {
                    shopify.getVariants(this.viewController.blueprint, productId).then((variants) => {
                        this.products[productId] = variants;

                        this.variants.push(...variants);

                        this.checkOrderableSku();
                    });
                }
            }
        },

        checkOrderableSku() {
            const variant = this.variants.find((x) => x.sku === this.productSku);

            this.orderableBase = null;

            if (variant) {
                if (variant.ats > 0) {
                    this.orderableBase = variant;
                }
            }

            // Check other orderable items.
            const needToLoad = [];

            this.viewController.state.forEach((s) => {
                if (s.component.custom?.['baa-cart-item']) {
                    const id = s.component.custom?.['baa-cart-product'];
                    // const sku = s.component.placementIndependentCode.replace(/-/g, ' ');

                    if (this.products[id] == null) {
                        if (!needToLoad.includes(id)) {
                            needToLoad.push(id);
                        }
                    }
                }
            });

            if (needToLoad.length > 0) {
                Promise.all(needToLoad.map((id) => new Promise((resolve) => {
                    shopify.getVariants(this.viewController.blueprint, id).then((variants) => {
                        this.products[id] = variants;

                        this.variants.push(...variants);

                        resolve();
                    });
                }))).then(() => {
                    this.checkCartItems();
                });
            } else {
                this.checkCartItems();
            }
        },

        checkCartItems() {
            const orderable = {};

            if (this.orderableBase) {
                orderable[this.orderableBase.sku] = {
                    id: this.orderableBase.id,
                    sku: this.orderableBase.sku,
                    price: this.orderableBase.price,
                    need: 1,
                    ats: this.orderableBase.ats,
                    base: true,
                };
            }

            this.viewController.state.forEach((s) => {
                if (s.component.custom?.['baa-cart-item']) {
                    const sku = s.component.placementIndependentCode.replace(/-/g, ' ');

                    if (orderable[sku] == null) {
                        orderable[sku] = {
                            sku,
                            need: 0,
                        };
                    }

                    orderable[sku].need += 1;

                    const variant = this.variants.find((x) => x.sku === sku);
                    if (variant) {
                        orderable[sku].id = variant.id;
                        orderable[sku].ats = variant.ats;
                        orderable[sku].price = variant.price;
                    }
                }
            });

            this.cartItems = orderable;

            // eslint-disable-next-line
            console.log('Orderable: ', this.cartItems);
        },

        /**
         * Continue initializing the controller and configure product colors.
         */
        loadColors({ controllers }) {
            let showController = null;

            if (controllers.length > 0) {
                // eslint-disable-next-line
                showController = controllers[0];
            }

            if (showController) {
                if (this.startingController == null) {
                    // First time initialization, restore initial selections from WebTop and options.
                    this.setInitialSelections(showController);

                    // Make a copy of the first complete controller.
                    // `loadColors` may be called several times, for example when changing the traction system, so only capture the first controller.
                    this.startingController = showController.clone();
                }

                this.showController(showController);

                // Check if there are any assemblies that are already loaded, and if not, select something.
                // this.loadDefaultAssemblies();
            }
        },

        /**
         * Sets initial selections.
         */
        setInitialSelections() {
        },

        /**
         * Sets initial colors from WebTop instance.
         */
        setInitialColors() {
        },

        /**
         * Sets initial text.
         */
        setInitialText() {
        },

        /**
         * Set initial logo.
         */
        setInitialLogo() {
        },

        /**
         * Sets initial size and width for both shoes.
         */
        setInitialSizeWidth() {
        },

        /**
         * Loads defaults assemblies for the current controller.
         */
        loadDefaultAssemblies() {
        },

        /**
         * Reloads controller with a different set of products.
         */
        reloadController(product) {
            return new Promise((resolve) => {
                const reloaded = new this.options.DriveCustomizer.Controller({
                    ...this.defaultControllerOptions(),
                    ...{
                        products: [product],
                    },
                });

                reloaded.on('afterInitializeState', () => {
                });

                reloaded.on('afterInitializeBlueprint', () => {
                    resolve(reloaded);
                });

                this.loadController(reloaded);
            });
        },

        /**
         * Selects a controller to show in the UI.
         */
        showController(controller) {
            this.viewController = controller;

            let pickView = null;
            if (this.currentView) {
                // Preserve the selected view.
                pickView = this.viewController?.blueprint?.views?.find((v) => v.code === this.currentView.code);
            }

            if (pickView == null) {
                pickView = this.viewController?.blueprint?.views?.[0];
            }

            this.setCurrentView(pickView);
        },

        /**
         * Reloads controllers with the new bundle while attempting to save the current printable configuration.
         */
        selectBundle(product) {
            this.reloadController(product).then((controller) => {
                this.showController(controller);
            });
        },

        /**
         * Puts the app in an error state if the user tries to add to cart before all selections have been made.
         * Using a count so the accordions will collapse when the Add to Cart button is clicked even when in the error state.
         */
        setCartErrors(numErrors) {
            this.cartErrors += numErrors;
        },

        /**
         * Puts the app in an error state if the user tries to add to cart before all selections have been made.
         */
        setErrorState(state) {
            this.inErrorState = state;
        },

        /**
         * Sets whether an error message should be displayed when a disabled add to cart button is clicked.
         */
        setHasBackorderedError() {
            this.hasBackorderedError = this.hasBackorderedSize;
        },

        /**
         * Sets whether a selected shoe size is backordered.
         */
        setHasBackorderedSize(hasBackorderedSize) {
            this.hasBackorderedSize = hasBackorderedSize;
        },

        /**
         * Used to show user feedback after attempting to add to their wishlist.
         */
        setAddToWishlistMessage(message) {
            this.addToWishlistMessage = message;
        },

        /**
         * Resets customizations.
         */
        resetCustomization() {
            // Reset to the very first loaded controller.
            const newBeginning = this.startingController.clone();

            // Reset events since the old handles might have closures capturing old data.
            newBeginning.off();
            this.attachControllerEvents(newBeginning);

            this.showController(newBeginning);

            this.undoStack = [];

            this.cartErrors = 0;
            this.inErrorState = false;
            this.hasBackorderedSize = false;
            this.hasBackorderedError = false;

            this.currentView = this.viewController?.blueprint?.views?.[0];
        },

        /**
         * Save undo checkpoint.
         */
        undoCheckpoint() {
            // Push the current state into the undo stack so it could be restored later.
            this.undoStack.push(this.viewController.clone());
        },

        /**
         * Stores the previous selection to be used for Undo functionality.
         * Updates the selected component.
         */
        setSelection(placementCode, optionValue) {
            this.undoCheckpoint();

            // Update the current controller.
            this.viewController.updateComponent(placementCode, optionValue);

            // Get out of error state if all selections have been made.
            if (this.selectionsComplete && this.inErrorState) {
                this.inErrorState = false;
            }
        },

        /**
         * Sets the current current group.
         */
        setCurrentGroup(groupName) {
            this.currentGroup = groupName;
        },

        /**
         * Sets the current camera view.
         */
        setCurrentView(view) {
            this.currentView = view;
        },

        /**
         * Sets the current camera view when a group is selected.
         */
        setCurrentViewByGroup(group) {
            const checkView = group.custom?.['fj-prefer-try-view'];

            if (checkView) {
                // Try to highlight this group.

                const materialName = `${checkView}_`;

                // emitter.$emit('highlightMeshes', {
                //     controller: this.viewController,
                //     meshes: (mesh) => {
                //         if (mesh?.material?.name.includes(checkView)) {
                //             return true;
                //         }

                //         return false;
                //     },
                //     color: ThreeUtilities.toColor3('#000000'),
                //     delay: 100,
                //     duration: 1200,
                //     transition: 500,
                // });

                emitter.$emit('clearOverlays');
                emitter.$emit('overlayMeshes', {
                    controller: this.viewController,
                    meshes: (mesh) => {
                        if (mesh?.material?.name.includes(materialName)) {
                            return true;
                        }

                        return false;
                    },
                    color: ThreeUtilities.toColor3(this.viewController.blueprint.custom['fj-highlight-color'] ?? '#000000'),
                    alpha: +(this.viewController.blueprint.custom['fj-highlight-alpha'] ?? 0.6),
                    delay: 100,
                    duration: 1000,
                    transition: 250,
                });

                const tryView = this.viewController.blueprint.views.find((view) => view.code === checkView);
                if (tryView?.custom?.['three-camera-alpha'] != null) {
                    this.currentView = tryView;

                    return;
                }
            }

            const selectedView = this.viewController.blueprint.views.find((view) => view.code === group.custom['fj-prefer-view']
                || view.name === group.custom['fj-prefer-view']);

            if (selectedView) {
                this.currentView = selectedView;
            }
        },

        /**
         * Return to the last made selection.
         */
        undoSelections() {
            if (this.undoStack.length > 0) {
                // Take the last captured controller and make it active.
                const lastState = this.undoStack.pop();

                this.showController(lastState);
            }
        },

        /**
         * Helper method, get a list of visible placements.
         */
        visiblePlacements(group) {
            if (group.placements == null) {
                return null;
            }

            return group.placements.filter((placement) => {
                // Skip hidden placements.
                if (placement?.custom?.['fj-hidden'] === 'true') {
                    return false;
                }

                // Skip placements with only one valid choice.
                if (placement?.components?.length <= 1) {
                    return false;
                }

                return true;
            });
        },

        /**
         * Saves current controller configuration as instance in WebTop.
         */
        saveInstance() {
            return new Promise((resolve) => {
                this.generateSnapshots().then((images) => {
                    this.saveRecipe(images).then((recipe) => {
                        resolve(recipe);
                    });
                });
            });
        },

        /**
         * Save customizer recipe.
         */
        saveRecipe(images) {
            return new Promise((resolve) => {
                this.viewController.saveRecipe({
                    translateRecipeAfter: (recipe) => {
                        // Save images.
                        images.forEach((image) => {
                            const view = recipe.views.find((v) => v.code === image.view && this.viewController.id === image.controller);

                            if (view) {
                                view.clientRender = image.data;
                            }
                        });

                        const sku = [];

                        this.viewController.state.forEach((s) => {
                            if (s.placement.custom?.['baa-sku'] === 'true') {
                                sku.push(s.component.placementIndependentCode);
                            }
                        });

                        recipe.custom['baa-sku'] = sku.join('-');

                        recipe.custom['baa-items'] = JSON.stringify(this.cartItems);
                    },
                }).then((recipe) => {
                    // Fetch the actual recipe.
                    fetch(recipe.location).then((response) => response.json()).then((data) => {
                        resolve(data);
                    });
                });
            });
        },

        /**
         * Creates a client side render of snapshots.
         */
        generateSnapshots() {
            return new Promise((resolve) => {
                const snapshots = [];

                // Create local snapshots.
                const controllers = [this.viewController];

                controllers.forEach((controller) => {
                    controller.blueprint.views.forEach((view) => {
                        if (view.bakeInRecipe && view.viewType === 'Camera') {
                            snapshots.push({
                                controller: controller.id,
                                view,
                            });
                        }
                    });
                });

                const images = [];

                this.prerender(snapshots, images, resolve);
            });
        },

        /**
         * Prerender a single view.
         */
        prerender(snapshots, images, resolve) {
            if (snapshots.length > 0) {
                const view = snapshots.shift();

                emitter.$emit('snapshot', {
                    controllerId: view.controller,
                    view: view.view.code,
                    done: (data) => {
                        images.push({
                            controller: view.controller,
                            view: view.view.code,
                            data,
                        });

                        this.prerender(snapshots, images, resolve);
                    },
                });
            } else {
                resolve(images);
            }
        },

        /**
         * Saves current controller configuration as instance in WebTop.
         */
        saveWebTopInstance(options) {
            return new Promise((resolve) => {
                // From what I can tell from existing services, these are hardcoded rather than generated on the fly based on the product configuration.
                // .... saddlecolor:n("saddle"),accentcolor:n("accent"),accent2color:n("accent2"),lacecolor:n("lace")}},t.ge ....

                const parameters = {};

                // Baseline parameters.

                parameters.vendorKey = this.options.vendor ?? '';
                parameters.sales_office_id = this.vendor.sales_office_id;
                parameters.branding_package_id = this.options.package ?? -1;
                parameters.custom_instance_id = 0;
                parameters.quantity = 1;

                // Selected product.
                parameters.sku = this.product.sku;

                // Selected size.
                const leftSize = this.viewController.selectedAtPlacement('LeftSizeWidth');
                if (leftSize?.custom?.['fj-size-value']) {
                    parameters.size_left = leftSize?.custom?.['fj-size-value'];
                } else {
                    parameters.size_left = '';
                }
                if (leftSize?.custom?.['fj-width-value']) {
                    parameters.width_left = leftSize?.custom?.['fj-width-value'];
                } else {
                    parameters.width_left = '';
                }

                const rightSize = this.viewController.selectedAtPlacement('RightSizeWidth');
                if (rightSize?.custom?.['fj-size-value']) {
                    parameters.size_right = rightSize?.custom?.['fj-size-value'];
                } else {
                    parameters.size_right = '';
                }
                if (rightSize?.custom?.['fj-width-value']) {
                    parameters.width_right = rightSize?.custom?.['fj-width-value'];
                } else {
                    parameters.width_right = '';
                }

                // In some cases the size selection is required.
                if (options && options.forceSize) {
                    if (parameters.size_left === '' || parameters.width_left === '') {
                        const firstSize = this.viewController.findPlacement('LeftSizeWidth').components.find((x) => !x.placeholder
                            && this.viewController.isAvailable(x));

                        if (firstSize) {
                            parameters.size_left = firstSize?.custom?.['fj-size-value'];
                            parameters.width_left = firstSize?.custom?.['fj-width-value'];
                        }
                    }

                    if (parameters.size_right === '' || parameters.width_right === '') {
                        const firstSize = this.viewController.findPlacement('RightSizeWidth').components.find((x) => !x.placeholder
                            && this.viewController.isAvailable(x));

                        if (firstSize) {
                            parameters.size_right = firstSize?.custom?.['fj-size-value'];
                            parameters.width_right = firstSize?.custom?.['fj-width-value'];
                        }
                    }
                }

                // Personalization parameters.
                parameters.logo_left = '';
                parameters.logo_right = '';

                parameters.personalization_text_left = '';
                parameters.personalization_text_right = '';

                parameters.script_type = '';

                parameters.threadcolor = '';

                // Add personalization.
                const left = this.viewController.blueprint.groups.find((x) => x.code === 'PersonalizationLeft');
                const right = this.viewController.blueprint.groups.find((x) => x.code === 'PersonalizationRight');

                if (left && right) {
                    const leftType = this.selectedPersonalizationType(left);
                    const rightType = this.selectedPersonalizationType(right);

                    const leftSelected = this.selectedPersonalization(left);
                    const rightSelected = this.selectedPersonalization(right);

                    if (leftType === 'personalization-logo') {
                        if (leftSelected?.custom?.['fj-parameter-value']) {
                            parameters.logo_left = leftSelected.custom['fj-parameter-value'];
                        }
                    }

                    if (rightType === 'personalization-logo') {
                        if (rightSelected?.custom?.['fj-parameter-value']) {
                            parameters.logo_right = rightSelected.custom['fj-parameter-value'];
                        }
                    }

                    if (leftType === 'personalization-monogram') {
                        if (leftSelected?.custom?.['fj-script-value']) {
                            parameters.script_type = leftSelected.custom['fj-script-value'];
                        }

                        if (leftSelected?.custom?.['fj-parameter-value']) {
                            parameters.threadcolor = leftSelected.custom['fj-parameter-value'];
                        }

                        parameters.personalization_text_left = leftSelected.custom['personalization-text'];
                    }

                    if (rightType === 'personalization-monogram') {
                        if (rightSelected?.custom?.['fj-script-value']) {
                            parameters.script_type = rightSelected.custom['fj-script-value'];
                        }

                        if (rightSelected?.custom?.['fj-parameter-value']) {
                            parameters.threadcolor = rightSelected.custom['fj-parameter-value'];
                        }

                        parameters.personalization_text_right = rightSelected.custom['personalization-text'];
                    }
                }

                // Hrm. WebTop really wants these values to be filled in.
                // Should we pull them from the default config?
                parameters.accent2color = '';
                parameters.accentcolor = '';
                parameters.basecolor = '';
                parameters.lacecolor = '';
                parameters.saddlecolor = '';

                if (this.product?.defaultInspiration) {
                    const hasColor = (product, group, color) => {
                        const styleGroup = product.styleGroups.find((x) => x.parameterName === group);

                        if (styleGroup?.options) {
                            const option = styleGroup.options.find((x) => x.ecomValue === color);

                            if (option && option.active && option.available) {
                                return true;
                            }
                        }

                        return false;
                    };

                    [
                        ['accentcolor', '$accent'],
                        ['accent2color', '$accent2'],
                        ['basecolor', '$base'],
                        ['lacecolor', '$lace'],
                        ['saddlecolor', '$saddle'],
                    ].forEach(([v, group]) => {
                        // Don't set default values if the style group is not attached to the product.
                        const styleGroup = this.product.styleGroups.find((x) => x.parameterName === group);
                        if (styleGroup == null) {
                            return;
                        }

                        if (this.product.defaultInspiration[v] && hasColor(this.product, group, this.product.defaultInspiration[v])) {
                            parameters[v] = this.product.defaultInspiration[v];
                        }

                        if (parameters[v] === '') {
                            if (hasColor(this.product, group, '827')) {
                                parameters[v] = '827';
                            }
                        }

                        if (parameters[v] === '') {
                            if (hasColor(this.product, group, '468')) {
                                parameters[v] = '468';
                            }
                        }

                        if (parameters[v] === '') {
                            if (hasColor(this.product, group, '437')) {
                                parameters[v] = '437';
                            }
                        }

                        if (parameters[v] === '') {
                            if (styleGroup?.options && styleGroup.options.length > 0) {
                                const groupOptions = [...styleGroup.options].filter((x) => x.active && x.available);

                                groupOptions.sort((a, b) => a.id - b.id);

                                parameters[v] = groupOptions[0].ecomValue;
                            }
                        }
                    });
                }

                // Components.
                this.viewController.state.forEach((s) => {
                    if (s.placement.custom?.['fj-parameter-name'] != null) {
                        const parameter = `${s.placement.custom?.['fj-parameter-name'].replace('$', '')}color`;

                        if (s.component.custom?.['fj-parameter-value'] != null) {
                            parameters[parameter] = s.component.custom?.['fj-parameter-value'];
                        }
                    }
                });

                // Save WebTop instance.
                fetch(getServiceUrl('save-instance', this.options), {
                    method: 'post',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        vendor: this.options.vendor,
                        environment: this.options.environment,
                        parameters,
                    }),
                })
                    .then((response) => response.json())
                    .then((instanceData) => {
                        resolve({
                            webTopConfiguration: parameters,
                            instance: instanceData,
                        });
                    });
            });
        },

        /**
         * Recreate WebTop configuration object from a recipe, but also add stuff such as image data.
         */
        formatDataFromRecipe(recipe) {
            const configuration = JSON.parse(recipe.custom['fj-configuration']);

            // Add images.
            let imageIndex = 0;

            recipe.views.forEach((view) => {
                if (view.previewJpg) {
                    const imageId = `myJoysImageUrl${imageIndex}`;

                    let url = view.previewJpg;

                    if (url.startsWith('//')) {
                        url = `https:${url}`;
                    }

                    configuration[imageId] = url;

                    imageIndex += 1;
                }
            });

            // Echoed parameters.

            if (this.options.lineItemId) {
                configuration.lineItemId = this.options.lineItemId;
            }

            if (this.options.instance) {
                configuration.originalInstanceId = this.options.instance;
            }

            if (this.options.originalMasterId) {
                configuration.originalMasterId = this.options.originalMasterId;
            }

            if (this.viewController?.blueprint?.custom?.['fj-master-product']) {
                configuration.pdpMasterProduct = this.viewController?.blueprint?.custom?.['fj-master-product'];
            }

            // Recipe ID for tracking.

            configuration.driveRecipe = recipe.id;

            return configuration;
        },

        /**
         * Sends WebTop data to whoever needs it.
         */
        useInstanceService(serviceType, serviceUrl, webTopData) {
            if (serviceType == null || serviceUrl == null) {
                return Promise.resolve();
            }

            return new Promise((resolve) => {
                // Use JSONP.
                if (serviceType === 'JSONP') {
                    const url = new URL(serviceUrl);

                    Object.entries(webTopData).forEach(([k, v]) => {
                        url.searchParams.append(k, v);
                    });

                    // Whelp, services always use `angular.callbacks._0` callback.

                    window.angular = {
                        callbacks: {
                            _0: (response) => {
                                // eslint-disable-next-line
                                console.log('Service response:', response);

                                resolve(response);
                            },
                        },
                    };

                    const script = document.createElement('script');
                    script.src = url.toString();
                    document.head.appendChild(script);
                } else {
                    // Otherwise fallback to regular POST.
                    fetch(serviceUrl, {
                        method: 'post',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(webTopData),
                    })
                        .then((response) => response.json())
                        .then((data) => {
                            resolve(data);
                        });
                }
            });
        },

        /**
         * Add to cart, notify the host, and exit.
         */
        addToCartAndExit() {
            const appStore = useAppStore();

            appStore.setIsBusy(true);

            this.saveInstance().then((recipe) => {
                // eslint-disable-next-line
                console.log('Saved', recipe);

                if (this.options.onAddToCart) {
                    this.options.onAddToCart(recipe);
                }
            }).catch(() => {
                appStore.setIsBusy(false);
            });
        },

        /**
         * Save an instance, notify the host to save design, and exit.
         */
        saveAndExit() {
            const appStore = useAppStore();

            appStore.setIsBusy(true);

            this.saveInstance().then((recipe) => {
                const webTopData = this.formatDataFromRecipe(recipe);

                const continueSave = () => {
                    // There is no standard flow for save design, the host should provide onSaveDesign callback.
                    let canClose = true;

                    if (this.options.onSavedDesign) {
                        if (this.options.onSavedDesign(webTopData)) {
                            canClose = false;
                        }
                    }

                    if (canClose) {
                        this.globalDone();
                    }
                };

                if (this.options.onSaveDesign) {
                    const result = this.options.onSaveDesign(webTopData);

                    if (result instanceof Promise) {
                        result.then(() => {
                            continueSave();
                        });
                    } else {
                        continueSave();
                    }
                } else {
                    continueSave();
                }
            }).catch(() => {
                appStore.setIsBusy(false);
            });
        },

        /**
         * Save an instance and copy the generated link to clipboard.
         */
        saveAndCopyLink() {
            const appStore = useAppStore();

            appStore.setIsBusy(true);

            this.saveInstance({
                instanceOnly: true,
            }).then((instance) => {
                const continueSave = () => {
                    let url;

                    if (window.location.href.includes('demandware.store')) {
                        url = `${this.buildBaseUrl()}/MyJoys-Customize?customInstanceId=${instance.instance}`;
                    } else {
                        url = `${this.buildBaseUrl()}/myjoys-customize?customInstanceId=${instance.instance}`;
                    }

                    navigator.clipboard.writeText(url);

                    appStore.setIsBusy(false);
                    appStore.setLinkCopied(true);
                };

                continueSave();
            }).catch(() => {
                appStore.setIsBusy(false);
            });
        },

        buildBaseUrl() {
            // Test mode.
            if (window.location.href.includes('demandware.store')) {
                if (window.CQuotient) {
                    // eslint-disable-next-line max-len
                    return `${window.location.protocol}//${window.location.host}/on/demandware.store/Sites-${window.CQuotient.siteId}-Site/${window.CQuotient.locale}`;
                }
            }

            // Does it look like that the site is using /s/ urls with locales?
            if (/^\/s\/footjoy[a-z-]*\/[a-z][a-z]_[A-Z][A-Z]\//.test(window.location.pathname)) {
                const locale = `${window.location.pathname.split('/').slice(0, 4).join('/')}`;

                return `${window.location.protocol}//${window.location.host}${locale}`;
            }

            // Does it look like that the site is using SFCC /s/ urls without locales?
            if (/^\/s\/footjoy/.test(window.location.pathname)) {
                const locale = `${window.location.pathname.split('/').slice(0, 3).join('/')}`;

                return `${window.location.protocol}//${window.location.host}${locale}`;
            }

            // Does it look like that the site is using locales?
            if (/\/[a-z][a-z]_[A-Z][A-Z]\//.test(window.location.pathname)) {
                const secondSlash = window.location.pathname.indexOf('/', 1);

                const locale = window.location.pathname.substr(0, secondSlash);

                return `${window.location.protocol}//${window.location.host}${locale}`;
            }

            // Fallback.
            return `${window.location.protocol}//${window.location.host}`;
        },

        /**
         * Save an instance and open print page in new tab.
         */
        saveAndPrint() {
            const appStore = useAppStore();

            appStore.setIsBusy(true);

            this.saveInstance({
                forceSize: true,
            }).then((recipe) => {
                const webTopData = this.formatDataFromRecipe(recipe);
                const instanceId = webTopData.custom_instance_id;

                const continueSave = () => {
                    appStore.setIsBusy(false);

                    window.open(`https://www.footjoy.com/myjoys/printfriendly/${this.options.vendor}/${instanceId}/?&price=1`, '_blank');
                };

                continueSave();
            }).catch(() => {
                appStore.setIsBusy(false);
            });
        },

        /**
         * Save an instance and sends an email.
         */
        saveAndShare(options) {
            return new Promise((resolve) => {
                const appStore = useAppStore();

                appStore.setIsBusy(true);

                this.saveInstance({
                    forceSize: true,
                }).then((recipe) => {
                    const webTopData = this.formatDataFromRecipe(recipe);
                    const instanceId = webTopData.custom_instance_id;

                    const parameters = {
                        to_email: options.toEmail,
                        to_name: options.to,
                        from_email: options.fromEmail,
                        from_name: options.from,
                        copy_me: options.sendCopy,
                        message: options.message,
                        subject: this.useTranslations('EmailToFriendSubject'),
                        salutation: this.useTranslations('EmailToFriendSalutation'),
                        custom_instance_id: instanceId,
                        sales_office_id: this.vendor.sales_office_id,
                        vendorKey: this.options.vendor ?? '',
                        myJoysImageUrl0: webTopData.myJoysImageUrl0,
                        myJoysImageUrl1: webTopData.myJoysImageUrl1,
                        myJoysImageUrl2: webTopData.myJoysImageUrl2,
                        myJoysImageUrl3: webTopData.myJoysImageUrl3,
                        myJoysImageUrl4: webTopData.myJoysImageUrl4,
                        myJoysImageUrl5: webTopData.myJoysImageUrl5,
                        myJoysImageUrl6: webTopData.myJoysImageUrl6,
                    };

                    // Save WebTop instance.
                    fetch(getServiceUrl('send-to-friend', this.options), {
                        method: 'post',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            vendor: this.options.vendor,
                            environment: this.options.environment,
                            parameters,
                        }),
                    })
                        .then(() => {
                            appStore.setIsBusy(false);

                            resolve();
                        });
                }).catch(() => {
                    appStore.setIsBusy(false);
                });
            });
        },

        /**
         * Save an instance, notify the host to add to wishlist, and exit.
         */
        addToWishlistAndExit() {
            const appStore = useAppStore();

            appStore.setIsBusy(true);

            this.saveInstance().then((recipe) => {
                const webTopData = this.formatDataFromRecipe(recipe);

                let standardFlow = true;

                const continueSave = () => {
                    if (standardFlow) {
                        // Follows the standard flow using the service configured on the vendor object.
                        let serviceType = 'POST';

                        if (this.vendor?.cartServiceType) {
                            serviceType = this.vendor.cartServiceType;
                        }

                        if (this.options.cartServiceType) {
                            serviceType = this.options.cartServiceType;
                        }

                        let serviceUrl = null;

                        if (this.vendor?.wishlistUrl) {
                            serviceUrl = this.vendor.wishlistUrl;
                        }

                        if (this.options.wishlistUrl) {
                            serviceUrl = this.options.wishlistUrl;
                        }

                        this.useInstanceService(serviceType, serviceUrl, webTopData).then((response) => {
                            let canClose = true;

                            if (this.options.onAddedToWishlist) {
                                let looksLikeJson = response;

                                try {
                                    looksLikeJson = JSON.parse(looksLikeJson);
                                } catch (e) {
                                    // Nothing.
                                }

                                if (this.options.onAddedToWishlist(webTopData, looksLikeJson)) {
                                    canClose = false;
                                    appStore.setIsBusy(false);
                                    // If user is not logged in, set message so modal is displayed
                                    if (!looksLikeJson.forwardUser) {
                                        this.setAddToWishlistMessage(looksLikeJson);
                                    } else {
                                        // User is already logged in, take them to their wishlist
                                        this.setAddToWishlistMessage(null);
                                        window.open(looksLikeJson.url, '_self');
                                    }
                                }
                            }

                            if (canClose) {
                                this.globalDone();
                            }
                        });
                    } else {
                        let canClose = true;

                        if (this.options.onAddedToWishlist) {
                            if (this.options.onAddedToWishlist(webTopData)) {
                                canClose = false;
                            }
                        }

                        if (canClose) {
                            this.globalDone();
                        }
                    }
                };

                if (this.options.onAddToWishlist) {
                    const result = this.options.onAddToWishlist(webTopData);

                    if (result instanceof Promise) {
                        result.then((resolved) => {
                            if (resolved) {
                                standardFlow = false;
                            }

                            continueSave();
                        });
                    } else {
                        if (result) {
                            standardFlow = false;
                        }

                        continueSave();
                    }
                } else {
                    continueSave();
                }
            });
        },

        /**
         * The widget has done whatever it was doing and now it is time to close. Should we destroy the instance or just keep it
         * (for example if the host page redirects anyway).
         */
        globalDone() {
            const appStore = useAppStore();

            appStore.globalDone();
        },

        /**
         * Checks if customer prefers a different sizes from the start.
         */
        preferDifferentSizes() {
            return this.options.preferDifferentSizes;
        },

        /**
         * Checks if component is backordered and returns the availability date.
         */
        isBackordered(component) {
            if (this.backorderLookup[component.code]) {
                return this.backorderLookup[component.code];
            }

            return false;
        },

        /**
         * Sets instance error flag.
         */
        setInstanceError(isError) {
            this.instanceError = isError;
        },

        /**
         * Clear personalization from a placement.
         */
        clearPlacement(controller, placementCode) {
            const selectedComponent = controller.selectedAtPlacement(placementCode);
            const selectedPlacement = controller.findPlacement(placementCode);

            if (selectedComponent.custom['dynamic-image-url'] && selectedComponent.custom['dynamic-image-url'].length > 0) {
                controller.updateDynamicImage(placementCode, {
                    title: '',
                    name: '',
                    url: '',
                    pngUrl: '',
                    jpgUrl: '',
                    width: '',
                    height: '',
                });
            } else if (selectedComponent.custom['personalization-text'] && selectedComponent.custom['personalization-text'].length > 0) {
                controller.updatePersonalization(placementCode, '');
            } else {
                const placeholder = selectedPlacement.components.find((x) => x.placeholder);

                if (placeholder) {
                    controller.updateComponent(placementCode, placeholder.code);
                }
            }

            controller.commitCustomAttributes(placementCode, {
                'personalization-filled': '',
            }, {
                propagate: true,
            });

            controller.clearComponent(placementCode);
        },

        isPersonalizationPlacement(placement) {
            return placement.code.startsWith('MONOGRAMMINGLOGO')
                || placement.code.startsWith('MONOGRAMMINGIMAGE')
                || placement.code.startsWith('MONOGRAMMINGTEXT');
        },

        isPersonalizationGroup(group) {
            return group.placements.some((placement) => this.isPersonalizationPlacement(placement));
        },

        isBlankPersonalization(group) {
            return group.placements.every((placement) => {
                if (this.isPersonalizationPlacement(placement)) {
                    const selected = this.viewController.selectedAtPlacement(placement.code);

                    if (!selected?.custom['personalization-filled']) {
                        return true;
                    }
                }

                return false;
            });
        },

        selectedPersonalizationType(group) {
            const logoPlacement = group.placements.find((x) => x.code.startsWith('MONOGRAMMINGLOGO'));

            if (logoPlacement) {
                if (ComponentUtilities.isFilled(this.viewController.selectedAtPlacement(logoPlacement.code))) {
                    return 'personalization-logo';
                }
            }

            const textPlacement = group.placements.find((x) => x.code.startsWith('MONOGRAMMINGTEXT'));

            if (textPlacement) {
                if (ComponentUtilities.isFilled(this.viewController.selectedAtPlacement(textPlacement.code))) {
                    return 'personalization-monogram';
                }
            }

            return 'personalization-none';
        },

        selectedPersonalization(group) {
            const filledPlacement = group.placements.find((placement) => {
                if (this.isPersonalizationPlacement(placement)) {
                    const selected = this.viewController.selectedAtPlacement(placement.code);

                    return ComponentUtilities.isFilledish(selected);
                }

                return null;
            });

            if (filledPlacement) {
                const filled = this.viewController.selectedAtPlacement(filledPlacement.code);

                return filled;
            }

            return null;
        },

        clearPersonalization(group) {
            if (group.placements) {
                group.placements.forEach((placement) => {
                    if (this.isPersonalizationPlacement(placement)) {
                        this.clearPlacement(this.viewController, placement.code);
                    }
                });
            }
        },

        markPersonalized(placement) {
            if (placement) {
                this.viewController.commitCustomAttributes(placement.code, {
                    'personalization-filled': 'true',
                }, {
                    propagate: true,
                });
            }
        },

        setMirrorPersonalization(v) {
            this.mirrorPersonalization = v;
        },

        mirrorToAnotherSide(group) {
            // Mirror to another side.
            let otherGroup;

            if (group.code.includes('Left')) {
                otherGroup = this.viewController.blueprint.groups.find((x) => x.code === 'PersonalizationRight');
            } else {
                otherGroup = this.viewController.blueprint.groups.find((x) => x.code === 'PersonalizationLeft');
            }

            this.clearPersonalization(otherGroup);

            const selectedType = this.selectedPersonalizationType(group);

            // Find a similar placement on the other side.
            if (selectedType === 'personalization-logo') {
                let thisSide;
                let otherSide;

                if (group.code.includes('Left')) {
                    thisSide = this.viewController.findPlacement('MONOGRAMMINGLOGO21');
                    otherSide = this.viewController.findPlacement('MONOGRAMMINGLOGO11');
                } else {
                    thisSide = this.viewController.findPlacement('MONOGRAMMINGLOGO11');
                    otherSide = this.viewController.findPlacement('MONOGRAMMINGLOGO21');
                }

                const thisComponent = this.viewController.selectedAtPlacement(thisSide.code);

                if (thisComponent && !thisComponent.placeholder) {
                    const otherComponent = otherSide.components.find((x) => x.placementIndependentCode === thisComponent.placementIndependentCode);

                    if (otherComponent) {
                        this.viewController.updateComponent(otherSide.code, otherComponent.code);
                    }
                }
            }

            if (selectedType === 'personalization-monogram') {
                let thisSide;
                let otherSide;

                if (group.code.includes('Left')) {
                    thisSide = this.viewController.findPlacement('MONOGRAMMINGTEXT21');
                    otherSide = this.viewController.findPlacement('MONOGRAMMINGTEXT11');
                } else {
                    thisSide = this.viewController.findPlacement('MONOGRAMMINGTEXT11');
                    otherSide = this.viewController.findPlacement('MONOGRAMMINGTEXT21');
                }

                const thisComponent = this.viewController.selectedAtPlacement(thisSide.code);

                if (thisComponent && !thisComponent.placeholder) {
                    const otherComponent = otherSide.components.find((x) => x.placementIndependentCode === thisComponent.placementIndependentCode);

                    if (otherComponent) {
                        this.viewController.updateComponent(otherSide.code, otherComponent.code);

                        const text = thisComponent.custom['personalization-text'];

                        if (text) {
                            this.viewController.updatePersonalization(otherSide.code, text);
                        }
                    }
                }
            }
        },

        make360Video(duration) {
            emitter.$emit('make360Video', duration);
        },

        setPreviewMode(mode) {
            this.previewMode = mode;
        },
    },
});
